/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Google.Headings = NO -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1704326400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Jan 04 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "firestore",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Firestore",
          "depth": 0,
          "id": "connect-to-firestore"
        }, {
          "value": "Firebase",
          "depth": 1,
          "id": "firebase"
        }, {
          "value": "Google Cloud Platform",
          "depth": 1,
          "id": "google-cloud-platform"
        }, {
          "value": "Custom database name",
          "depth": 1,
          "id": "custom-database-name"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Columns to sync",
          "depth": 1,
          "id": "columns-to-sync"
        }, {
          "value": "Delete mode",
          "depth": 1,
          "id": "delete-mode"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      a: "a",
      h3: "h3",
      strong: "strong",
      img: "img",
      pre: "pre",
      code: "code"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: "Push new records and update records that change in your source"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-firestore",
        children: "Connect to Firestore"
      }), "\n", _jsxs(_components.p, {
        children: ["You can decide whether to set up Firestore with ", _jsx(_components.a, {
          href: "#firebase",
          children: "Firebase"
        }), " or with ", _jsx(_components.a, {
          href: "#google-cloud-platform",
          children: "Google Cloud Platform"
        }), ".\nThe following sections outline the necessary setup for either service."]
      }), "\n", _jsx(_components.h3, {
        id: "firebase",
        children: "Firebase"
      }), "\n", _jsxs(_components.p, {
        children: ["To find the credentials, navigate to ", _jsx(_components.a, {
          href: "https://console.firebase.google.com",
          children: "Firestore"
        }), " and select your project."]
      }), "\n", _jsxs(_components.p, {
        children: ["In the left-hand sidebar, click the wheel icon next to ", _jsx(_components.strong, {
          children: "Project Overview"
        }), " and select ", _jsx(_components.strong, {
          children: "Project settings"
        }), " from the menu."]
      }), "\n", _jsxs(_components.p, {
        children: ["Once on the Project setting screen, select the ", _jsx(_components.strong, {
          children: "Service accounts"
        }), " tab. Make sure you are on the ", _jsx(_components.strong, {
          children: "Firebase Admin SDK"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You will need to click the ", _jsx(_components.strong, {
          children: "Generate new private key"
        }), " button to download a JSON file of the credentials."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-firebase-firestore-credentials.png",
          alt: "Firestore Firebase credentials"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In Hightouch, paste your ", _jsx(_components.strong, {
          children: "private key"
        }), " including the header and footer in the following format:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "-----BEGIN PRIVATE KEY-----\n<private key body>\n<private key body>\n<private key body>\n-----END PRIVATE KEY-----\\n\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Make sure to include the final ", _jsx(_components.code, {
          children: "\\n"
        }), " character as well, as that is still part of the private key."]
      }), "\n", _jsx(_components.h3, {
        id: "google-cloud-platform",
        children: "Google Cloud Platform"
      }), "\n", _jsxs(_components.p, {
        children: ["To find the credentials, navigate to ", _jsx(_components.a, {
          href: "https://console.cloud.google.com/firestore/welcome",
          children: "Google Cloud Platform"
        }), ". In the header, next to Google Cloud Platform, click the ", _jsx(_components.strong, {
          children: "Select a project"
        }), " dropdown and select a project."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you do not have Firestore set up within this project, you will be taken to the ", _jsx(_components.strong, {
          children: "Get started"
        }), " screen. Click the ", _jsx(_components.strong, {
          children: "Select Native Mode"
        }), " button."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-firestore-get-started-api.png",
          alt: "Firestore GCP Get started"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Select a location from the dropdown, and then click the ", _jsx(_components.strong, {
          children: "Create Database"
        }), " button."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-firestore-get-started-location.png",
          alt: "Firestore GCP Database"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once you are redirected to the Firestore dashboard screen, click the ", _jsx(_components.strong, {
          children: "Security Rules"
        }), " in the sidebar. Click on the ", _jsx(_components.strong, {
          children: "Enable Firebase"
        }), " button, and confirm by clicking the ", _jsx(_components.strong, {
          children: "Enable"
        }), " link within the modal."]
      }), "\n", _jsxs(_components.p, {
        children: ["Click on the hamburger menu in the top left corner. Select ", _jsx(_components.strong, {
          children: "APIs & Services"
        }), " from the sidebar, and then select ", _jsx(_components.strong, {
          children: "Credentials"
        }), " from the dropdown menu."]
      }), "\n", _jsxs(_components.p, {
        children: ["Once on the Credentials screen, scroll to the ", _jsx(_components.strong, {
          children: "Service Accounts"
        }), " table. Click on the edit icon in the row with the ", _jsx(_components.strong, {
          children: "Name"
        }), " as ", _jsx(_components.strong, {
          children: "App Engine default service account"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-firestore-service-accounts.png",
          alt: "Firestore GCP Service Accounts"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Click on the ", _jsx(_components.strong, {
          children: "Keys"
        }), " tab, and then click the ", _jsx(_components.strong, {
          children: "Add Key"
        }), " button."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-firestore-keys.png",
          alt: "Firestore GCP Keys"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Select the ", _jsx(_components.strong, {
          children: "Create new key"
        }), " option. Keep the ", _jsx(_components.strong, {
          children: "Key type"
        }), " as ", _jsx(_components.strong, {
          children: "JSON"
        }), ", and click the ", _jsx(_components.strong, {
          children: "Create"
        }), " button in the modal. This will download a JSON with the needed credentials."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-firestore-create-key.png",
          alt: "Firestore GCP Create key"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["In Hightouch, paste your ", _jsx(_components.strong, {
          children: "private key"
        }), " including the header and footer in the following format:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "-----BEGIN PRIVATE KEY-----\n<private key body>\n<private key body>\n<private key body>\n-----END PRIVATE KEY-----\\n\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Make sure to include the final ", _jsx(_components.code, {
          children: "\\n"
        }), " character as well, as that is still part of the private key."]
      }), "\n", "\n", _jsx(_components.h3, {
        id: "custom-database-name",
        children: "Custom database name"
      }), "\n", _jsxs(_components.p, {
        children: ["Firestore defaults to the ", _jsx(_components.code, {
          children: "(default)"
        }), " database. If your setup requires a custom database name enter it in the ", _jsx(_components.strong, {
          children: "Database Name"
        }), " field in Hightouch during setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports the Upsert sync mode."
      }), "\n", _jsx(_components.p, {
        children: "During the Upsert mode, new rows will be inserted into Firestore and existing rows will be kept up-to-date within Firestore."
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Records can be matched from your source to Firestore by any unique property."
      }), "\n", _jsx(_components.h3, {
        id: "columns-to-sync",
        children: "Columns to sync"
      }), "\n", _jsx(_components.p, {
        children: "For Firestore, we give you the ability to send all columns as they are represented in your model."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-firestore-sync-columns.png",
          alt: "Firestore columns"
        })
      }), "\n", _jsx(_components.p, {
        children: "You can also choose to manually map fields. Only the fields that you map will be exported. All other columns from your results are ignored."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-firestore-add-mappings.png",
          alt: "Firestore mappings"
        })
      }), "\n", _jsx(_components.h3, {
        id: "delete-mode",
        children: "Delete mode"
      }), "\n", _jsx(_components.p, {
        children: "The default is to do nothing, but you can also set Hightouch to delete the record when it leaves the query result."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
